import WorkspaceStore from 'iam/WorkspaceStore'
import {
  WorkspaceQueryPort,
  MemberQueryPort,
  ListWorkspaceMembersQuery,
  ListWorkspacesQuery,
  AlertPort,
  UserQueryPort,
  DownloaderPort,
} from './ports'
import once from 'lodash/once'
import MemberStore from 'iam/MemberStore'
import { GetCurrentUser } from 'iam/SessionStore'
import { AlertFactory } from 'alerts/Alert'
import { CursorPaged, Id } from 'types/definitions'
import { StreamDownloader } from '../../infra/StreamDownloader'
import { CancellationToken } from '../../infra/cancellation'

export class PayoutsAdminModuleAdapter
  implements
    WorkspaceQueryPort,
    MemberQueryPort,
    AlertPort,
    UserQueryPort,
    DownloaderPort
{
  constructor(
    public readonly newAlert: AlertFactory,
    public readonly getCurrentUser: GetCurrentUser,
    public readonly downloader: StreamDownloader,
    private workspaceStore: WorkspaceStore,
    private memberStore: MemberStore
  ) {
    this.newAlert = newAlert
  }

  // There is an issue in the Workspace Find API that doesn't
  // paginate correctly. Until that is fixed, we just query for
  // all workspaces and memoize it.
  listPlatformWorkspaces = (query: ListWorkspacesQuery) =>
    this._memoizedListPlatformWorkspaces().then((page) => {
      const filtered = filterPaged(page, query.searchText, (w) => w.name)
      return CursorPaged.fromArray(
        filtered.data.filter((w) => w.config.payouts?.enabled)
      )
    })

  listWorkspaceMembers = (
    query: ListWorkspaceMembersQuery,
    ct = CancellationToken.default
  ) =>
    this.memberStore.search(
      {
        workspace_id: Id.assert(query.workspaceId),
        limit: query.pagination.limit,
        after: query.pagination.after,
        search_text: query.searchText,
      },
      ct
    )

  private _memoizedListPlatformWorkspaces = once(() =>
    this.workspaceStore.find({
      after: null,
      limit: 10_000,
    })
  )
}

/**
 * Filters a paged result.
 *
 * @param page
 * @param searchText
 * @param selector
 */
function filterPaged<T>(
  page: CursorPaged<T>,
  searchText: string | undefined,
  selector: (item: T) => string
): CursorPaged<T> {
  const lowerSearchText = searchText?.toLowerCase()
  if (!lowerSearchText) {
    return page
  }
  const matched = page.data.filter((item) =>
    selector(item).toLowerCase().includes(lowerSearchText)
  )
  return CursorPaged.fromArray(matched, page.cursor)
}
